export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
] as const;

/** Look up the full state name from its abbreviation (e.g. "CA" → "California"). */
export function getStateName(code: string | undefined | null): string {
  if (!code) return '';
  return US_STATES.find((s) => s.value === code)?.label || code;
}

/**
 * Canonical list of purchasing / selling unit names used across the app.
 * Keep this in one place — the product form, PO form, picker, and any new
 * unit dropdown should all import from here so they stay in sync. Adding
 * a new unit means adding it here, not in three forms.
 */
export const UNIT_OPTIONS = ['each', 'case', 'pallet'] as const;
export type UnitName = (typeof UNIT_OPTIONS)[number];

/**
 * True when `value` is one of the canonical UNIT_OPTIONS (case-insensitive,
 * trimmed). Useful when rendering a select that needs to fall back to a
 * legacy value (e.g. an older product saved with "kg") without silently
 * dropping it from the dropdown.
 */
export function isCanonicalUnit(value: string | null | undefined): value is UnitName {
  if (!value) return false;
  return (UNIT_OPTIONS as readonly string[]).includes(value.trim().toLowerCase());
}

/**
 * Return the select options to render for a unit dropdown, preserving any
 * legacy value the row already carries. If `current` isn't in UNIT_OPTIONS
 * (e.g. a product saved before the unit set was restricted), it's appended
 * so the user sees the existing value and isn't forced to re-pick blindly.
 */
export function unitOptionsFor(current: string | null | undefined): readonly string[] {
  const trimmed = (current ?? '').trim();
  if (!trimmed) return UNIT_OPTIONS;
  if ((UNIT_OPTIONS as readonly string[]).includes(trimmed.toLowerCase())) return UNIT_OPTIONS;
  return [...UNIT_OPTIONS, trimmed];
}

/**
 * UNIT_OPTIONS in size-ascending order — `each` is the base ("smallest"),
 * `case` packs many `each`, `pallet` packs many `case`. Used to constrain
 * the selling unit to ≤ the purchasing unit (you can't sell a pallet you
 * only ever bought a case of).
 */
export function allowedSellingUnitsFor(
  purchasingUnit: string | null | undefined,
  currentSellingUnit?: string | null,
): readonly string[] {
  const target = (purchasingUnit ?? '').trim().toLowerCase();
  const idx = (UNIT_OPTIONS as readonly string[]).indexOf(target);
  // Unknown / legacy purchasing unit → fall back to the full list so the
  // user can still set something sensible. The form's submit-time guard
  // covers truly invalid combos.
  if (idx < 0) return unitOptionsFor(currentSellingUnit);
  const allowed = UNIT_OPTIONS.slice(0, idx + 1);
  // Preserve a legacy current value (e.g. "kg") so the dropdown doesn't
  // silently drop it.
  const currentTrimmed = (currentSellingUnit ?? '').trim();
  if (currentTrimmed && !(allowed as readonly string[]).includes(currentTrimmed.toLowerCase())) {
    return [...allowed, currentTrimmed];
  }
  return allowed;
}

/**
 * Canonical list of CRV (California Redemption Value) fee amounts, in cents.
 * Used by the product form and the department default picker. Stored on the
 * product as an integer cents value; 0 means "no CRV". Keep the list in
 * ascending order so the picker buttons read naturally left-to-right.
 */
export const CRV_FEE_OPTIONS = [0, 5, 10, 15, 20, 25, 50, 60, 75, 120, 200, 300] as const;

/** Label for a CRV option (cents). 0 → "None"; ≥100¢ shows as dollars. */
export function formatCrvOption(cents: number): string {
  if (cents === 0) return 'None';
  return cents >= 100 ? `$${(cents / 100).toFixed(2)}` : `${cents}¢`;
}
